import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Select, Radio, Upload, Button, App, Alert, Space, Steps, Input, DatePicker } from 'antd';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import { Upload as UploadIcon, Send, ArrowLeft, ArrowRight, Paperclip } from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { taskService } from '@/services/taskService';
import { attachmentService } from '@/services/attachmentService';
import { signingService, type SignatureRect } from '@/services/signingService';
import { userService } from '@/services/userService';
import { documentService, type DocumentCategory } from '@/services/documentService';
import { PdfSignaturePlacer } from '@/components/signing/PdfSignaturePlacer';
import { CategoryTreeSelect } from '@/components/documents/CategoryTreeSelect';

export function SigningCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // File pre-loaded từ Templates flow (Phase 3): location.state.preloadedFile = File
  // và location.state.suggestedTitle = string, suggestedCategoryId = uuid
  // Phase A: location.state.preResolvedSigners = ResolvedSignerSlot[] (pre-config từ template builder)
  // Phase B: location.state.signaturePositions = SignaturePositionMm[] (vị trí khung ký mm)
  const preloaded = (location.state as {
    preloadedFile?: File;
    suggestedTitle?: string;
    suggestedCategoryId?: string;
    preResolvedSigners?: Array<{
      label: string;
      slotIndex: number;
      mode: string;
      order?: number;
      required: boolean;
      signer?: { id: string; fullName: string; position?: string };
      resolveNote?: string;
    }>;
    signaturePositions?: Array<{
      page: number;
      slotIndex: number;
      label: string;
      x: number;     // mm
      y: number;
      width: number;
      height: number;
    }>;
  }) ?? {};

  // Phase A: Pre-extract signerIds đã resolve (mọi slot đã resolve ra NGƯỜI THẬT)
  // V1.0.64-fix: sort theo order (thứ tự ký từ template config) trước khi extract
  // V1.0.66-fix: KHÔNG loại slot 'required=false' nữa. Slot tùy chọn mà ĐÃ resolve ra người
  //   (vd: "Phụ trách bộ phận" theo vai trò → Hoàng Minh Loan) vẫn phải là người ký.
  //   'required' chỉ có ý nghĩa với slot KHÔNG resolve được (đã tự loại vì thiếu signer.id).
  //   Trước đây vế '&& s.required !== false' khiến hộp trên hiện 4 người mà danh sách ký chỉ 3.
  const preResolvedSignerIds: string[] = (preloaded.preResolvedSigners ?? [])
    .filter(s => s.signer?.id)
    .slice() // tránh mutate prop
    .sort((a, b) => {
      const oa = a.order ?? a.slotIndex;
      const ob = b.order ?? b.slotIndex;
      if (oa !== ob) return oa - ob;
      return a.slotIndex - b.slotIndex;
    })
    .map(s => s.signer!.id)
    .filter((v, i, arr) => arr.indexOf(v) === i); // dedupe (giữ first occurrence)

  /**
   * Phase B: tự động build map signerId → SignatureRect từ Phase A signers + Phase B positions.
   * Match qua slotIndex: preResolvedSigners[i].slotIndex ↔ signaturePositions[j].slotIndex
   * Convert mm → ratio (A4 = 210mm × 297mm)
   *
   * V1.0.63c: Trả về 2 cấu trúc:
   *  - prebuiltPositions (Record - LEGACY): 1 user 1 vị trí (cho UI hiển thị step 1)
   *  - prebuiltPositionList (Array - MỚI): tất cả vị trí, cho phép trùng user (cho submit BE)
   */
  const A4_W_MM = 210;
  const A4_H_MM = 297;
  const prebuiltPositionList: Array<{ signerId: string; rect: SignatureRect; slotIndex: number; order: number }> = (() => {
    const list: Array<{ signerId: string; rect: SignatureRect; slotIndex: number; order: number }> = [];
    if (!preloaded.signaturePositions || !preloaded.preResolvedSigners) return list;
    for (const sig of preloaded.preResolvedSigners) {
      if (!sig.signer?.id) continue;
      const pos = preloaded.signaturePositions.find(p => p.slotIndex === sig.slotIndex);
      if (!pos) continue;
      // V1.0.63c: KHÔNG dedup nữa — 1 user có thể có nhiều slot
      list.push({
        signerId: sig.signer.id,
        slotIndex: sig.slotIndex,
        // V1.0.64-fix: Lưu order từ signerConfig để sort theo thứ tự ký (không phải vị trí trên giấy)
        order: sig.order ?? sig.slotIndex,
        rect: {
          page: pos.page,
          xRatio: pos.x / A4_W_MM,
          yRatio: pos.y / A4_H_MM,
          wRatio: pos.width / A4_W_MM,
          hRatio: pos.height / A4_H_MM,
        },
      });
    }
    // V1.0.64-fix: Sort theo ORDER (thứ tự ký từ template) thay vì slotIndex (vị trí vẽ trên giấy)
    // Lý do: chữ ký trên giấy xếp trái→phải theo cấp bậc (Giám đốc trái, Người đề nghị phải)
    //        nhưng thứ tự ký logic là Người đề nghị → ... → Giám đốc cuối cùng.
    return list.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.slotIndex - b.slotIndex; // tiebreaker
    });
  })();

  // Legacy: Record signerId -> rect (chỉ giữ 1 vị trí/user, dùng cho UI chọn vị trí manual)
  const prebuiltPositions: Record<string, SignatureRect> = (() => {
    const out: Record<string, SignatureRect> = {};
    for (const item of prebuiltPositionList) {
      if (!out[item.signerId]) out[item.signerId] = item.rect;
    }
    return out;
  })();

  /** Phase B: Có đủ data để skip step kéo thả không? */
  const canSkipPositionStep = prebuiltPositionList.length > 0;

  const [users, setUsers] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>(() => {
    if (preloaded.preloadedFile) {
      const f = preloaded.preloadedFile;
      return [{
        uid: '-template-1',
        name: f.name,
        size: f.size,
        type: f.type,
        status: 'done' as const,
        originFileObj: f as any,
      }];
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step state
  const [currentStep, setCurrentStep] = useState(0);
  // V1.0.63i: Multi-position state - mỗi item có id duy nhất để xóa từng cái
  // Cấu trúc Array thay vì Record cho phép cùng signerId có nhiều rect
  const [positionList, setPositionList] = useState<Array<{ id: string; signerId: string; rect: SignatureRect }>>(() => {
    // Init từ prebuiltPositionList nếu có (template multi-slot)
    return prebuiltPositionList.map((p, i) => ({
      id: `init-${i}-${Date.now()}`,
      signerId: p.signerId,
      rect: p.rect,
    }));
  });
  const [selectedSignerId, setSelectedSignerId] = useState<string | null>(null);
  // V1.0.63i: File đính kèm tham chiếu (upload mới, không phải file ký)
  // Lưu list File[] - sẽ upload khi submit và gửi kèm relatedAttachmentIds
  const [relatedFiles, setRelatedFiles] = useState<UploadFile[]>([]);

  // Mirror form values — đảm bảo luôn có values dù Form instance có vấn đề gì
  const [formValues, setFormValues] = useState<{
    taskId?: string;
    title?: string;
    taskText?: string;
    signerIds?: string[];
    workflow?: 'SEQUENTIAL' | 'PARALLEL';
    categoryId?: string;
    dueDate?: any; // dayjs
  }>({
    workflow: 'SEQUENTIAL',
    title: preloaded.suggestedTitle,
    taskText: preloaded.suggestedTitle,
    categoryId: preloaded.suggestedCategoryId,
    signerIds: preResolvedSignerIds.length > 0 ? preResolvedSignerIds : undefined,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [usersResp, tasksResp, catsResp] = await Promise.all([
          // V1.0.59 FIX: tăng pageSize 200 → 1000 vì BV nhiều user, search không tìm thấy
          // Đồng thời để chắc chắn lấy hết: nếu total > pageSize sẽ fetch tiếp
          userService.list({ pageSize: 1000 }),
          taskService.list({ pageSize: 100 }).catch(() => ({ items: [] })),
          documentService.listCategories().catch(() => [] as DocumentCategory[]),
        ]);
        let allUsers = usersResp.items;
        // V1.0.59: Nếu tổng user > 1000, fetch tiếp các trang còn lại
        if ((usersResp as any).total && (usersResp as any).total > 1000) {
          const totalPages = Math.ceil((usersResp as any).total / 1000);
          for (let p = 2; p <= totalPages; p++) {
            try {
              const more = await userService.list({ page: p, pageSize: 1000 });
              allUsers = [...allUsers, ...more.items];
            } catch { break; }
          }
        }
        setUsers(allUsers);
        setTasks((tasksResp as any).items || []);
        setCategories(catsResp);
      } catch (e: any) {
        message.error('Không tải được dữ liệu: ' + (e.response?.data?.message ?? e.message));
      } finally { setLoading(false); }
    })();
    form.setFieldsValue({
      workflow: 'SEQUENTIAL',
      title: preloaded.suggestedTitle,
      taskText: preloaded.suggestedTitle,
      categoryId: preloaded.suggestedCategoryId,
    });
  }, []);

  const selectedSigners = useMemo(() => {
    // V1.0.64-fix: Map theo thứ tự signerIds (thứ tự ký người dùng chọn / từ template),
    // KHÔNG dùng users.filter() vì .filter() duyệt theo thứ tự của `users` array (alphabet).
    const ids = formValues.signerIds ?? [];
    const userMap = new Map(users.map(u => [u.id, u]));
    return ids
      .map(id => userMap.get(id))
      .filter((u): u is NonNullable<typeof u> => !!u)
      .map(u => ({ id: u.id, fullName: u.fullName }));
  }, [users, formValues.signerIds]);

  const pdfFile = fileList[0]?.originFileObj as File | undefined;

  const handleNextStep = async () => {
    try {
      await form.validateFields(['signerIds', 'workflow', 'taskText']);
    } catch {
      return;
    }
    if (fileList.length === 0) {
      message.warning('Vui lòng upload tài liệu PDF');
      return;
    }
    // Double check PDF (beforeUpload đã lọc, nhưng belt-and-suspenders)
    const pdfFile = fileList[0]?.originFileObj as File | undefined;
    if (!pdfFile || (pdfFile.type !== 'application/pdf' && !pdfFile.name.toLowerCase().endsWith('.pdf'))) {
      message.error('Tài liệu cần ký phải là file PDF');
      return;
    }
    // LƯU values vào state trước khi chuyển step
    const v = form.getFieldsValue();
    setFormValues({
      taskId: v.taskId || undefined,       // UUID từ Select (nếu user chọn)
      title: v.title || undefined,          // V1.0.66: Tiêu đề tài liệu
      taskText: v.taskText || undefined,    // Mô tả loại văn bản (bắt buộc)
      signerIds: v.signerIds,
      workflow: v.workflow,
      categoryId: v.categoryId,
      dueDate: v.dueDate,
    });
    if (v.signerIds && v.signerIds.length > 0) {
      // V1.0.64: Set selectedSignerId = signer đầu tiên theo THỨ TỰ KÝ
      // (v.signerIds[0] đã là signer đầu vì AntD Select multi giữ thứ tự click,
      //  + nếu là pre-resolved từ template thì preResolvedSignerIds đã sort theo order)
      setSelectedSignerId(v.signerIds[0]);
    }

    // Phase B: nếu tất cả signer đã có position pre-built → skip step kéo thả, sang thẳng review
    const signersWithPositions = new Set(positionList.map(p => p.signerId));
    const allSignersHavePos = (v.signerIds ?? []).every((sid: string) => signersWithPositions.has(sid));
    if (canSkipPositionStep && allSignersHavePos) {
      setCurrentStep(2);
      return;
    }

    setCurrentStep(1); // step 1 = chọn vị trí
  };

  const handleSubmit = async () => {
    const { taskId, title, taskText, signerIds, workflow, categoryId, dueDate } = formValues;

    console.log('[SigningCreate] Submit with formValues:', formValues);

    if (!signerIds || signerIds.length === 0) {
      message.error('Chưa chọn người ký. Vui lòng quay lại bước 1');
      return;
    }
    if (fileList.length === 0) {
      message.warning('Chưa có file');
      return;
    }

    setSubmitting(true);
    try {
      const file = fileList[0].originFileObj as File;
      message.loading({ content: 'Đang upload tài liệu...', key: 'submit' });

      // Nếu có taskId thật → upload vào task đó. Nếu không → dùng quick-attachment
      let attachmentId: string;
      if (taskId) {
        const attachment = await attachmentService.upload(taskId, file);
        attachmentId = attachment.id;
      } else {
        const attachment = await signingService.quickAttachment(file);
        attachmentId = attachment.id;
      }

      message.loading({ content: 'Đang tạo yêu cầu ký...', key: 'submit' });

      // V1.0.63i: Upload các file đính kèm tham chiếu (nếu có) trước để lấy attachment IDs
      let relatedIds: string[] = [];
      if (relatedFiles.length > 0) {
        message.loading({ content: `Đang upload ${relatedFiles.length} tài liệu đính kèm...`, key: 'submit' });
        for (const f of relatedFiles) {
          const fileObj = f.originFileObj as File;
          if (!fileObj) continue;
          try {
            // Upload qua endpoint quick-attachment (không cần task) → mỗi file thành 1 task_attachment
            const att = await signingService.quickAttachment(fileObj);
            relatedIds.push(att.id);
          } catch (err: any) {
            console.warn('Upload đính kèm lỗi:', err);
            // bỏ qua file lỗi, không chặn submit
          }
        }
      }

      // V1.0.63i: Luôn gửi signerPositionList - 1 user có thể có nhiều vị trí ký
      const req = await signingService.create({
        attachmentId,
        signerIds,
        workflow: workflow ?? 'SEQUENTIAL',
        taskId,
        title,
        taskDescription: taskText,
        categoryId,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
        // Gửi list các vị trí - mỗi item {signerId, rect}, có thể trùng signerId
        signerPositionList: positionList.length > 0
          ? positionList.map(p => ({ signerId: p.signerId, rect: p.rect }))
          : undefined,
        // V1.0.63i: Tài liệu tham chiếu đã upload
        relatedAttachmentIds: relatedIds.length > 0 ? relatedIds : undefined,
      });

      const totalPositions = positionList.length;
      message.success({
        content: totalPositions > signerIds.length
          ? `Đã gửi yêu cầu ký (${signerIds.length} người, ${totalPositions} vị trí ký)`
          : `Đã gửi yêu cầu ký tới ${signerIds.length} người`,
        key: 'submit',
        duration: 2,
      });
      setTimeout(() => navigate(`/signing/${req.id}`), 800);
    } catch (e: any) {
      message.error({
        content: e.response?.data?.message ?? e.message ?? 'Có lỗi',
        key: 'submit',
      });
    } finally { setSubmitting(false); }
  };

  const showStep2 = currentStep === 2;

  return (
    <>
      <PageHeader
        title="Trình ký mới"
        subtitle="Upload tài liệu PDF, chọn vị trí chữ ký và gửi cho người ký"
      />

      <div style={{ maxWidth: currentStep === 1 ? 1200 : 720 }}>
        <Card>
          <Steps
            current={currentStep}
            items={[
              { title: 'Thông tin & Người ký' },
              { title: 'Vị trí chữ ký trên PDF' },
              { title: 'Gửi' },
            ]}
            style={{ marginBottom: 24 }}
          />

          {/* Form LUÔN mounted, chỉ display:none khi không phải step 0.
              Điều này giữ form instance + values qua các step. */}
          <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
            <Form
              form={form}
              layout="vertical"
              disabled={loading}
              initialValues={{
                workflow: 'SEQUENTIAL',
                title: preloaded.suggestedTitle,
                taskText: preloaded.suggestedTitle,
                categoryId: preloaded.suggestedCategoryId,
                signerIds: preResolvedSignerIds.length > 0 ? preResolvedSignerIds : undefined,
              }}
              onValuesChange={(_, all) => {
                // Mirror values mỗi khi user thay đổi
                const taskValue = all.taskId as string | undefined;
                const isTaskIdUuid = taskValue && /^[0-9a-fA-F]{8}-/.test(taskValue);
                setFormValues({
                  taskId: isTaskIdUuid ? taskValue : undefined,
                  taskText: !isTaskIdUuid ? taskValue : undefined,
                  signerIds: all.signerIds,
                  workflow: all.workflow,
                  categoryId: all.categoryId,
                  dueDate: all.dueDate,
                });
              }}
            >
              <Alert
                message="Quy trình ký điện tử"
                description="1) Chọn loại văn bản + upload tài liệu + chọn người ký → 2) Đặt vị trí chữ ký (PDF) → 3) Gửi. Mỗi người sẽ nhận email OTP để xác thực. Sau khi ký xong, tài liệu tự động lưu trữ theo loại đã chọn."
                type="info" showIcon style={{ marginBottom: 20 }}
              />

              <Form.Item name="categoryId" label="Loại văn bản"
                rules={[{ required: true, message: 'Vui lòng chọn loại văn bản để tự động lưu trữ sau khi ký' }]}
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  Tài liệu sau khi ký xong sẽ tự tạo 1 bản ghi trong kho tài liệu theo loại này (VD: Hợp đồng HD-2026-04-001).
                </div>}>
                <CategoryTreeSelect
                  categories={categories}
                  placeholder="Chọn loại (Hợp đồng, Quyết định, Biên bản...)"
                />
              </Form.Item>

              <Form.Item name="dueDate" label="Thời hạn ký (tùy chọn)"
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  Thời điểm cần hoàn thành ký. Sau thời hạn này, yêu cầu sẽ được đánh dấu quá hạn nhưng vẫn có thể ký.
                </div>}>
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Chọn ngày giờ..."
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>

              <Form.Item name="taskId" label="Task liên quan (tùy chọn)"
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  💡 Chọn task sẵn có từ danh sách để liên kết trình ký này với task.
                </div>}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Tìm theo mã hoặc tên task..."
                  optionFilterProp="label"
                  options={tasks.map((t: any) => ({
                    value: t.id,
                    // label text hiển thị khi đã chọn — ưu tiên title cho dễ đọc
                    label: t.title,
                  }))}
                  // Render item trong dropdown: 2 dòng code + title
                  optionRender={(option) => {
                    const task = tasks.find((t: any) => t.id === option.value);
                    if (!task) return option.label;
                    return (
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{task.title}</div>
                        <div className="mono" style={{ fontSize: 10, color: '#8a95a5' }}>{task.code}</div>
                      </div>
                    );
                  }}
                  // Filter match cả code + title
                  filterOption={(input, option) => {
                    const t = tasks.find((task: any) => task.id === option?.value);
                    if (!t) return false;
                    const q = input.toLowerCase();
                    return (t.title ?? '').toLowerCase().includes(q)
                      || (t.code ?? '').toLowerCase().includes(q);
                  }}
                />
              </Form.Item>

              {/* V1.0.66: Tiêu đề tài liệu — tách khỏi Mô tả; cũng là TÊN khi lưu vào kho */}
              <Form.Item name="title" label="Tiêu đề tài liệu"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tài liệu' }]}
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  💡 Tên ngắn gọn của tài liệu (VD: "Hợp đồng mua máy chủ Q2/2026"). Đây cũng là TÊN tài liệu khi tự lưu vào Quản lý tài liệu sau khi ký xong.
                </div>}>
                <Input placeholder="VD: Quyết định bổ nhiệm Trưởng phòng CNTT" maxLength={500} />
              </Form.Item>

              <Form.Item name="taskText" label="Mô tả loại văn bản cần ký"
                rules={[{ required: true, message: 'Vui lòng nhập mô tả loại văn bản cần ký' }]}
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  💡 Mô tả nội dung và mục đích tài liệu (VD: "Hợp đồng mua máy chủ Q2/2026 với nhà cung cấp FPT").
                </div>}>
                <Input.TextArea
                  rows={3}
                  placeholder="Nhập mô tả chi tiết loại văn bản, mục đích, bối cảnh..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>

              <Form.Item label="Tài liệu cần ký" required
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  ⚠ Chỉ chấp nhận file <b>PDF</b>. Nếu tài liệu ở định dạng Word/Excel, vui lòng chuyển sang PDF trước khi upload.
                </div>}>
                <Upload.Dragger
                  fileList={fileList}
                  accept=".pdf,application/pdf"
                  beforeUpload={(file) => {
                    const isPdf = file.type === 'application/pdf'
                      || file.name.toLowerCase().endsWith('.pdf');
                    if (!isPdf) {
                      message.error(`File "${file.name}" không phải PDF. Vui lòng chuyển sang PDF trước khi upload.`);
                      return Upload.LIST_IGNORE;
                    }
                    // 50MB limit
                    if (file.size > 50 * 1024 * 1024) {
                      message.error(`File "${file.name}" vượt quá 50MB.`);
                      return Upload.LIST_IGNORE;
                    }
                    return false; // giữ trong fileList, không auto upload
                  }}
                  maxCount={1}
                  onChange={({ fileList }) => setFileList(fileList.slice(-1))}
                  onRemove={() => { setFileList([]); return true; }}
                >
                  <div style={{ padding: 20 }}>
                    <UploadIcon size={32} style={{ color: '#6699CC' }} />
                    <div style={{ marginTop: 10, fontSize: 13 }}>Kéo thả file PDF hoặc bấm để chọn</div>
                    <div style={{ fontSize: 11, color: '#8a95a5' }}>Chỉ PDF · tối đa 50MB</div>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              {/* V1.0.63i: Tài liệu tham chiếu (đính kèm thêm để người ký xem trước khi ký) */}
              <Form.Item label="Tài liệu tham chiếu (tùy chọn)"
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  📎 Đính kèm các tài liệu <b>không phải tài liệu ký</b> để người ký xem tham chiếu trước khi ký (vd: tờ trình, bảng dự toán, biên bản họp...). Hỗ trợ mọi định dạng.
                </div>}>
                <Upload.Dragger
                  multiple
                  fileList={relatedFiles}
                  beforeUpload={(file) => {
                    // 50MB limit mỗi file
                    if (file.size > 50 * 1024 * 1024) {
                      message.error(`File "${file.name}" vượt quá 50MB.`);
                      return Upload.LIST_IGNORE;
                    }
                    return false; // không upload ngay, giữ trong list để submit upload sau
                  }}
                  onChange={({ fileList }) => setRelatedFiles(fileList)}
                  onRemove={(file) => {
                    setRelatedFiles(prev => prev.filter(f => f.uid !== file.uid));
                    return true;
                  }}
                >
                  <div style={{ padding: '12px 0' }}>
                    <Paperclip size={28} style={{ color: '#6699CC' }} />
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      Kéo thả hoặc bấm để chọn file tham chiếu (có thể chọn nhiều)
                    </div>
                    <div style={{ fontSize: 11, color: '#8a95a5' }}>
                      Mọi định dạng · tối đa 50MB/file
                    </div>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              {/* Phase A: Banner thông báo signers đã pre-fill từ template */}
              {preloaded.preResolvedSigners && preloaded.preResolvedSigners.length > 0 && (
                <div style={{
                  background: '#f0f5ff', border: '1px solid #adc6ff',
                  borderRadius: 8, padding: 12, marginBottom: 16,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1d39c4', marginBottom: 8 }}>
                    {canSkipPositionStep
                      ? '✨ Người ký + Vị trí ký đã được cấu hình sẵn từ mẫu'
                      : '✍️ Người ký đã được cấu hình sẵn từ mẫu'}
                  </div>
                  <div style={{ fontSize: 12, color: '#5b6875', marginBottom: 8 }}>
                    {canSkipPositionStep
                      ? `Hệ thống đã tự fill ${preResolvedSignerIds.length} người ký + đặt sẵn ${Object.keys(prebuiltPositions).length} vị trí ký trên PDF. 🔒 Người ký đã khóa — chỉ cần xác nhận và gửi.`
                      : `Hệ thống đã tự fill ${preResolvedSignerIds.length} người ký theo cấu hình của mẫu. 🔒 Người ký đã khóa — không thể đổi tại đây.`}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {preloaded.preResolvedSigners.map((s, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                        padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e8edf2',
                      }}>
                        <span style={{
                          background: '#1976D2', color: '#fff', borderRadius: '50%',
                          width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                        }}>{s.order ?? i + 1}</span>
                        <span style={{ fontWeight: 600, color: '#1a1e23', minWidth: 140 }}>{s.label}</span>
                        {s.signer ? (
                          <>
                            <span style={{ color: '#1a1e23' }}>{s.signer.fullName}</span>
                            {s.signer.position && (
                              <span style={{ color: '#7d8a98' }}>· {s.signer.position}</span>
                            )}
                            <span style={{
                              marginLeft: 'auto', fontSize: 10, fontWeight: 600,
                              padding: '2px 6px', borderRadius: 4,
                              background: s.mode === 'FIXED_USER' ? '#e6f4ff'
                                : s.mode === 'CREATOR' ? '#fff7e6'
                                : '#f6ffed',
                              color: s.mode === 'FIXED_USER' ? '#1976D2'
                                : s.mode === 'CREATOR' ? '#d46b08'
                                : '#389e0d',
                            }}>
                              {s.mode === 'FIXED_USER' ? '👤 Cố định'
                                : s.mode === 'CREATOR' ? '✍️ Người tạo'
                                : '🎯 Theo vai trò'}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#cf1322', fontStyle: 'italic' }}>
                            {s.resolveNote ?? 'Chưa resolve được — chọn thủ công'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Form.Item name="signerIds" label="Người ký"
                rules={[{ required: true, message: 'Chọn ít nhất 1 người ký' }]}
                extra={preResolvedSignerIds.length > 0 ? (
                  <span style={{ fontSize: 11, color: '#1d39c4', fontWeight: 500 }}>
                    🔒 Người ký đã được cấu hình sẵn từ mẫu — không thể đổi tại đây.
                  </span>
                ) : undefined}>
                <Select mode="multiple" showSearch optionFilterProp="label"
                  placeholder="Chọn 1 hoặc nhiều người ký..."
                  // V1.0.27: Khi tạo phiếu từ template có pre-config signers → khóa dropdown
                  disabled={preResolvedSignerIds.length > 0}
                  options={users.map(u => ({
                    value: u.id,
                    label: `${u.fullName} — ${u.position ?? ''} (${u.department?.name ?? ''})`,
                  }))}
                />
              </Form.Item>

              <Form.Item name="workflow" label="Luồng ký" rules={[{ required: true }]}
                extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                  <b>Tuần tự:</b> ký theo thứ tự · <b>Song song:</b> ai ký trước cũng được
                </div>}>
                <Radio.Group>
                  <Radio.Button value="SEQUENTIAL">Tuần tự</Radio.Button>
                  <Radio.Button value="PARALLEL">Song song</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Space style={{ marginTop: 16 }}>
                <Button onClick={() => navigate('/signing')}>Hủy</Button>
                <Button type="primary" icon={<ArrowRight size={14} />}
                  onClick={handleNextStep}>
                  {canSkipPositionStep
                    ? 'Tiếp: Xem lại & Gửi yêu cầu ký'
                    : 'Tiếp: Chọn vị trí chữ ký'}
                </Button>
              </Space>
            </Form>
          </div>

          {/* STEP 1: PDF Placer */}
          {currentStep === 1 && pdfFile && (
            <div>
              <Alert
                message={`Đặt vị trí chữ ký cho ${selectedSigners.length} người ký`}
                description={<>
                  Chọn <b>người ký</b> bên trái, sau đó <b>click</b> vào vị trí trên PDF để đặt chữ ký.
                  Có thể <b>click nhiều lần</b> để đặt nhiều vị trí cho cùng 1 người.
                  Nếu không đặt, chữ ký sẽ tự động chèn vào góc dưới phải trang cuối.
                </>}
                type="info" showIcon style={{ marginBottom: 16 }}
              />

              <div style={{ height: 600 }}>
                <PdfSignaturePlacer
                  file={pdfFile}
                  signers={selectedSigners}
                  positionList={positionList}
                  onAddPosition={(signerId, rect) => {
                    setPositionList(prev => [
                      ...prev,
                      { id: `${signerId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, signerId, rect },
                    ]);
                  }}
                  onRemovePosition={(positionId) => {
                    setPositionList(prev => prev.filter(p => p.id !== positionId));
                  }}
                  selectedSignerId={selectedSignerId}
                  onSelectSigner={setSelectedSignerId}
                />
              </div>

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Button icon={<ArrowLeft size={14} />} onClick={() => setCurrentStep(0)}>
                  Quay lại
                </Button>
                <Space>
                  <Button onClick={() => setPositionList([])} disabled={positionList.length === 0}>
                    Xóa tất cả vị trí
                  </Button>
                  <Button type="primary" icon={<ArrowRight size={14} />}
                    onClick={() => setCurrentStep(2)}>
                    Tiếp: Gửi yêu cầu ký
                  </Button>
                </Space>
              </div>
            </div>
          )}

          {/* STEP 2: Confirm & Submit */}
          {showStep2 && (
            <div>
              <Alert
                message="Xác nhận gửi"
                description={<>
                  Sau khi gửi, mỗi người ký sẽ nhận email thông báo.
                  {positionList.length > 0 && <>
                    <br />Đã đặt <b>{positionList.length}</b> vị trí chữ ký cho <b>{new Set(positionList.map(p => p.signerId)).size}/{selectedSigners.length}</b> người ký.
                    Những người chưa đặt sẽ dùng vị trí mặc định (góc dưới phải trang cuối).
                  </>}
                </>}
                type="success" showIcon style={{ marginBottom: 16 }}
              />

              <div style={{ fontSize: 13, background: '#f8f9fa', padding: 16, borderRadius: 6 }}>
                <div style={{ marginBottom: 8 }}><b>Tài liệu:</b> {fileList[0]?.name ?? '(không có)'}</div>
                <div style={{ marginBottom: 8 }}>
                  <b>Loại văn bản:</b>{' '}
                  {categories.find(c => c.id === formValues.categoryId)
                    ? `${categories.find(c => c.id === formValues.categoryId)?.code} — ${categories.find(c => c.id === formValues.categoryId)?.name}`
                    : <span style={{ color: '#ff4d4f' }}>(chưa chọn)</span>}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>Task:</b>{' '}
                  {formValues.taskId
                    ? (tasks.find((t: any) => t.id === formValues.taskId)?.title ?? formValues.taskId)
                    : formValues.taskText
                      ? <><i>(nhập tự do)</i> "{formValues.taskText}"</>
                      : <span style={{ color: '#8a95a5' }}>(không có)</span>}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>Người ký ({selectedSigners.length}):</b>{' '}
                  {selectedSigners.map(s => s.fullName).join(', ') || '(chưa chọn)'}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <b>Luồng ký:</b> {formValues.workflow === 'SEQUENTIAL' ? 'Tuần tự' : 'Song song'}
                </div>
                <div>
                  <b>Thời hạn ký:</b>{' '}
                  {formValues.dueDate
                    ? formValues.dueDate.format('DD/MM/YYYY HH:mm')
                    : <span style={{ color: '#8a95a5' }}>(không giới hạn)</span>}
                </div>
              </div>

              {(!formValues.categoryId || selectedSigners.length === 0) && (
                <Alert
                  type="error"
                  message="Thiếu thông tin"
                  description="Vui lòng quay lại bước 1 để chọn loại văn bản và người ký."
                  style={{ marginTop: 12 }}
                  showIcon
                />
              )}

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Button icon={<ArrowLeft size={14} />}
                  onClick={() => setCurrentStep(1)}>
                  Quay lại
                </Button>
                <Button type="primary" icon={<Send size={14} />}
                  onClick={handleSubmit} loading={submitting}
                  disabled={selectedSigners.length === 0 || !formValues.categoryId}>
                  Gửi yêu cầu ký ({selectedSigners.length} người)
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

